import React, { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { 
  ArrowLeft, 
  Info, 
  MessageSquare, 
  ListTodo, 
  FileText, 
  Users, 
  Activity, 
  Star, 
  BarChart3,
  Mail,
  Phone,
  Plus,
  Upload,
  UserPlus,
  UserCheck,
  X,
  Share2,
  Send
} from "lucide-react";
import { Icons } from "@/components/icons";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Menu } from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useSidebar } from "@/context/SidebarContext";
import { NavigationContext } from "@/hooks/useNavigationContext";
import { useShareLists } from "@/hooks/useShareLists";

interface UnifiedSidebarProps {
  context: NavigationContext;
  leadName?: string;
  activeTab?: string;
  onTabChange?: (tab: string) => void;
}

const useMainNavItems = () => {
  const { data: shareLists } = useShareLists();
  
  const items: any[] = [
    { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: Icons.home, adminOnly: false, highlighted: false },
    { id: 'leads', label: 'Leads', path: '/app', icon: Icons.mapPin, adminOnly: false, highlighted: false },
    { id: 'shared-leads-all', label: 'All Shared Leads', path: '/shared-with-me', icon: Share2, adminOnly: false, highlighted: false, parent: 'leads' },
  ];
  
  // Add individual share lists
  if (shareLists) {
    shareLists.forEach((share) => {
      items.push({
        id: `share-${share.id}`,
        label: share.name || 'Untitled Share',
        path: `/shared-with-me/${share.id}`,
        icon: Share2,
        adminOnly: false,
        highlighted: false,
        parent: 'leads',
        title: share.name || 'Untitled Share',
      });
    });
  }
  
  items.push(
    { id: 'tasks', label: 'Tasks', path: '/tasks', icon: ListTodo, adminOnly: false, highlighted: false },
    { id: 'companies', label: 'Companies', path: '/companies', icon: Icons.building, adminOnly: false, highlighted: false },
    { id: 'mail-merges', label: 'Mail Merges', path: '/mail-merges', icon: Mail, adminOnly: false, highlighted: false },
    { id: 'documentation', label: 'Documentation', path: '/documentation', icon: FileText, adminOnly: false, highlighted: false },
    { id: 'admin', label: 'Admin', path: '/admin', icon: Icons.settings, adminOnly: true, highlighted: false },
  );
  
  return items;
};

const leadDetailTabs = [
  { id: 'overview', label: 'Overview', icon: Info },
  { id: 'notes', label: 'Notes', icon: MessageSquare },
  { id: 'tasks', label: 'Tasks', icon: ListTodo },
  { id: 'documents', label: 'Documents', icon: FileText },
  { id: 'contacts', label: 'Contacts', icon: Users },
  { id: 'activity', label: 'Activity', icon: Activity },
  { id: 'reviews', label: 'Reviews', icon: Star },
  { id: 'score', label: 'Score', icon: BarChart3 },
  { id: 'outreach', label: 'Outreach', icon: Send },
];

const UnifiedSidebar = ({
  context,
  leadName,
  activeTab: propActiveTab,
  onTabChange: propOnTabChange,
}: UnifiedSidebarProps) => {
  const { profile, user } = useAuth();
  const mainNavItems = useMainNavItems();
  const { 
    isOpen, 
    setIsOpen, 
    activeTab: contextActiveTab, 
    onTabChange: contextOnTabChange,
    leadName: contextLeadName,
    onOpenCommunication 
  } = useSidebar();
  const navigate = useNavigate();
  const isAdmin = profile?.role === 'admin';

  // Use prop values if provided, otherwise use context values
  const activeTab = propActiveTab ?? contextActiveTab;
  const onTabChange = propOnTabChange ?? contextOnTabChange;
  const currentLeadName = leadName ?? contextLeadName;

  const [subscriptionTier, setSubscriptionTier] = useState(() => {
    const sub = JSON.parse(localStorage.getItem('subscription') || '{"tier":"free"}');
    return sub.tier || 'free';
  });

  useEffect(() => {
    const load = () => {
      const sub = JSON.parse(localStorage.getItem('subscription') || '{"tier":"free"}');
      setSubscriptionTier(sub.tier || 'free');
    };
    window.addEventListener('storage', load);
    window.addEventListener('subscriptionUpdated', load);
    return () => {
      window.removeEventListener('storage', load);
      window.removeEventListener('subscriptionUpdated', load);
    };
  }, []);

  const getSubscriptionBadge = () => {
    switch (subscriptionTier) {
      case 'starter': return 'Starter';
      case 'pro': return 'Pro';
      case 'unlimited': return 'Unlimited';
      default: return 'Free';
    }
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  const handleTabClick = (tabId: string) => {
    onTabChange?.(tabId);
    setIsOpen(false);
  };

  const handleBackToLeads = () => {
    navigate('/app');
    setIsOpen(false);
  };

  // Render content based on context
  const renderContent = () => {
    if (context === 'lead-details') {
      return (
        <div className="space-y-6">
          {/* Lead Header */}
          <div className="space-y-3">
            <div className="px-2">
              <h3 className="font-semibold text-base truncate mb-1">{currentLeadName || 'Lead Details'}</h3>
              <p className="text-xs text-muted-foreground">Manage this lead</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="w-full justify-start text-primary hover:text-primary hover:bg-primary/10"
              onClick={handleBackToLeads}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to All Leads
            </Button>
          </div>

          <Separator />

          {/* Sections */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2">
              Sections
            </h4>
            <nav className="space-y-0.5">
              {leadDetailTabs.map((tab) => (
                <Button
                  key={tab.id}
                  variant="ghost"
                  size="sm"
                  className={cn(
                    'w-full justify-start h-9 font-normal',
                    activeTab === tab.id && 'bg-accent text-accent-foreground font-medium'
                  )}
                  onClick={() => handleTabClick(tab.id)}
                >
                  <tab.icon className="h-4 w-4 mr-3" />
                  {tab.label}
                </Button>
              ))}
            </nav>
          </div>

          <Separator />

          {/* Communication */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2">
              Communication
            </h4>
            <nav className="space-y-0.5">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start h-9 font-normal"
                onClick={() => {
                  onOpenCommunication?.('email');
                  setIsOpen(false);
                }}
              >
                <Mail className="h-4 w-4 mr-3" />
                Email Template
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start h-9 font-normal"
                onClick={() => {
                  onOpenCommunication?.('call');
                  setIsOpen(false);
                }}
              >
                <Phone className="h-4 w-4 mr-3" />
                Call Script
              </Button>
            </nav>
          </div>

          <Separator />

          {/* Quick Actions */}
          <div className="space-y-2">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2">
              Quick Actions
            </h4>
            <nav className="space-y-0.5">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start h-9 font-normal"
                onClick={() => {
                  onTabChange?.('notes');
                  setIsOpen(false);
                }}
              >
                <Plus className="h-4 w-4 mr-3" />
                Add Note
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start h-9 font-normal"
                onClick={() => {
                  onTabChange?.('documents');
                  setIsOpen(false);
                }}
              >
                <Upload className="h-4 w-4 mr-3" />
                Upload Document
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start h-9 font-normal"
                onClick={() => {
                  onTabChange?.('contacts');
                  setIsOpen(false);
                }}
              >
                <UserPlus className="h-4 w-4 mr-3" />
                Add Contact
              </Button>
            </nav>
          </div>
        </div>
      );
    }

    // Main navigation
    return (
      <div className="space-y-6">
        {/* Quick Actions Section - Add Lead at Top */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2">
            Quick Actions
          </h4>
          <Button
            variant="default"
            size="sm"
            className="w-full justify-center h-10 font-semibold shadow-md hover:shadow-lg transition-all bg-gradient-to-r from-primary via-primary to-primary/90"
            onClick={() => handleNavClick('/places')}
          >
            <Plus className="h-5 w-5 mr-2" />
            Add Lead
          </Button>
        </div>

        <Separator />

        {/* Main Navigation Items */}
        <div className="space-y-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2">
            Navigation
          </h4>
          <nav className="space-y-0.5">
            {mainNavItems.map((item) => {
              if (item.adminOnly && !isAdmin) return null;
              const isChild = 'parent' in item && item.parent;
              
              return (
                <Button
                  key={item.id}
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "w-full justify-start h-9 font-normal",
                    isChild && "pl-8 h-8 text-xs"
                  )}
                  onClick={() => handleNavClick(item.path)}
                  title={item.title}
                >
                  <item.icon className={cn("mr-3 shrink-0", isChild ? "h-3.5 w-3.5" : "h-4 w-4")} />
                  <span className={cn(isChild && "truncate")}>{item.label}</span>
                </Button>
              );
            })}
          </nav>
        </div>

        {/* Pricing Link - Only show for non-logged in users */}
        {!user && (
          <>
            <Separator />
            <div className="space-y-2">
              <Button
                variant="ghost"
                size="sm"
                className="w-full justify-start h-9 font-normal"
                onClick={() => handleNavClick('/pricing')}
              >
                <Icons.dollar className="h-4 w-4 mr-3" />
                Pricing
              </Button>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="md:hidden">
          <Menu className="h-5 w-5" />
          <span className="sr-only">Toggle menu</span>
        </Button>
      </SheetTrigger>
      <SheetContent side="left" className="p-0 flex flex-col max-h-screen [&>button]:hidden">
        {/* Header */}
        <SheetHeader className="px-4 py-5 border-b flex-shrink-0">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2">
              <Icons.logo className="h-5 w-5" />
              <span className="text-base">Discovery Hub</span>
            </SheetTitle>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 -mr-2"
              onClick={() => setIsOpen(false)}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Close</span>
            </Button>
          </div>
        </SheetHeader>
        
        {/* Scrollable Content */}
        <div className="flex-1 overflow-y-auto scrollbar-hide px-4 py-4">
          {renderContent()}
        </div>
        
        {/* Footer */}
        <div className="border-t px-4 py-3 mt-auto flex-shrink-0">
          <div className="flex items-center justify-between gap-2">
            <Link
              to="/profile"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity flex-1 min-w-0"
              onClick={() => setIsOpen(false)}
            >
              <Avatar className="h-8 w-8 flex-shrink-0">
                <AvatarImage src="" alt="Profile" />
                <AvatarFallback className="bg-primary/10">
                  <Icons.user className="h-4 w-4" />
                </AvatarFallback>
              </Avatar>
              <span className="text-sm font-medium truncate">Profile</span>
            </Link>
            <Link
              to="/subscription"
              onClick={() => setIsOpen(false)}
            >
              <Badge variant="outline" className="text-xs">
                {getSubscriptionBadge()}
              </Badge>
            </Link>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default UnifiedSidebar;
